import * as d3 from 'd3'
import _ from 'underscore'
import percentile from 'percentile'
import moment from 'moment'

import dateFormatter from '../../helper/dateFormatter'
import { timeScaleWeighted } from '../../helper/d3.weightedTimeScale'

let Chart = function() {
    var svg = null

    var brush = null

    var dimensions = {
        width: 0,
        height: 0,
    }

    var daysOnScale = 3
    var renderScrollbar = true
    var xAxisTicks = 20

    var margin = {
        top: 4,
        right: 40,
        bottom: 30,
        left: 40,
    }

    var lines = null

    var scales = null

    var markers = []

    var handlersRegistered = false

    this.create = function(rootElement, width, height, options) {
        svg = rootElement
        dimensions.width = width
        dimensions.height = height
        renderScrollbar = typeof options.renderScrollbar === 'boolean' ? options.renderScrollbar : true
        xAxisTicks = options.xAxisTicks || 20
        daysOnScale = options.daysOnScale || 3

        let d3Svg = d3
            .select(svg)
            .attr('class', 'chart')
            .attr('width', dimensions.width)
            .attr('height', dimensions.height)
        let container = d3Svg.append('g').attr('class', 'container')

        _initAxis(container)

        container.append('g').attr('class', 'historySelection')

        container.append('g').attr('class', 'textRanges')

        let lines = container.append('g').attr('class', 'lines')

        lines
            .append('clipPath')
            .attr('id', 'historyClipPath')
            .append('rect')
            .attr('x', 0)
            .attr('y', 0)
            .attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)

        lines
            .append('rect')
            .attr('class', 'background')
            .attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top)

        lines
            .append('rect')
            .attr('class', 'highlight')
            .attr('style', { display: 'none' })

        if (renderScrollbar) {
            let scrollBar = container.append('g').attr('class', 'scrollBar')

            scrollBar
                .append('rect')
                .attr('class', 'background')
                .attr('width', dimensions.width - margin.left - margin.right)
                .attr('height', 12)
        }
    }

    var _initAxis = function(root) {
        let axis = root.append('g').attr('class', 'axis')

        axis.append('g').attr('class', 'x')
        axis.append('g').attr('class', 'y')
        axis.append('g').attr('class', 'y2')
    }

    var _registerEventHandlers = function(root) {
        let highlight = d3.select(svg).select('.highlight')

        root.on('mouseover', function() {
            highlight.style('display', null)
        })
            .on('mouseout', function() {
                highlight.style('display', 'none')
            })
            .on('mousemove', function() {
                let [dataSet, nextDataSet] = _getSelectedItem(d3.mouse(this))

                if (dataSet) {
                    highlight
                        .attr('x', scales.x(dataSet.date))
                        .attr('y', 0)
                        .attr('width', scales.x(nextDataSet.date) - scales.x(dataSet.date))
                        .attr('height', dimensions.height - margin.top - margin.bottom)
                }
            })

        let start = [null, null]
        let selection = [null, null]
        let dragBehavior = d3
            .drag()
            .on('start', function() {
                let [dataSet, nextDataSet] = _getSelectedItem(d3.mouse(this))

                if (!dataSet) {
                    return
                }

                start[0] = selection[0] = dataSet
                start[1] = selection[1] = nextDataSet

                highlight
                    .attr('x', scales.x(selection[0].date))
                    .attr('y', 0)
                    .attr('width', scales.x(selection[1].date) - scales.x(selection[0].date))
                    .attr('height', dimensions.height - margin.top - margin.bottom)
            })
            .on('drag', function() {
                let [dataSet, nextDataSet] = _getSelectedItem(d3.mouse(this))

                if (!dataSet) {
                    return
                }

                if (nextDataSet.date > start[0].date) {
                    selection[1] = nextDataSet
                }

                if (dataSet.date < start[1].date) {
                    selection[0] = dataSet
                }

                highlight
                    .attr('x', scales.x(selection[0].date))
                    .attr('y', 0)
                    .attr('width', scales.x(selection[1].date) - scales.x(selection[0].date))
                    .attr('height', dimensions.height - margin.top - margin.bottom)
            })
            .on('end', function() {
                let [dataSet, nextDataSet] = _getSelectedItem(d3.mouse(this))

                if (!dataSet) {
                    return
                }

                if (nextDataSet.date > start[0].date) {
                    selection[1] = nextDataSet
                }

                if (dataSet.date < start[1].date) {
                    selection[0] = dataSet
                }

                jQuery(svg).trigger('rangeSelected', selection)
            })

        root.call(dragBehavior)
    }

    var _getSelectedItem = function(mouse) {
        let bisect = d3.bisector(function(d) {
            return d.date
        }).right

        let x0 = scales.x.invert(mouse[0])
        let dateItem = Math.min(bisect(lines[0].data, x0), lines[0].data.length - 1)
        let dataSet = lines[0].data[dateItem - 1]
        let nextDataSet = lines[0].data[dateItem]

        return [dataSet, nextDataSet]
    }

    var _scales = function(lines) {
        // http://bl.ocks.org/mbostock/4015254
        // http://bl.ocks.org/nnattawat/9689303
        const allDates = _.pluck(lines[1].data, 'date')
        const allResponseTimes = _.pluck(lines[1].data, 'value').filter(value => value > 0)
        const allRequests = _.pluck(lines[0].data, 'value')

        // y-axis should be at least 1, and either 200% 95p or max()
        let responseTimeMax = Math.max(Math.min(percentile(95, allResponseTimes) * 2, _.max(allResponseTimes)), 1)

        // some cases percentile calculation won't work
        if (isNaN(responseTimeMax)) {
            responseTimeMax = 1
        }

        return {
            x: d3
                .scaleTime()
                .domain([_.min(allDates), _.max(allDates)])
                .range([0, dimensions.width - margin.left - margin.right]),
            x2: d3
                .scaleTime()
                .domain([_.min(allDates), _.max(allDates)])
                .range([0, dimensions.width - margin.left - margin.right]),
            response: d3
                .scaleLinear()
                .domain([responseTimeMax, 0])
                .range([0, dimensions.height - margin.top - margin.bottom]),
            requests: d3
                .scaleLinear()
                .domain([Math.max(_.max(allRequests), 1), 0])
                .range([0, dimensions.height - margin.top - margin.bottom]),
        }
    }

    var _axis = function(scales) {
        return {
            x: d3
                .axisBottom(scales.x)
                .tickSizeInner(-(dimensions.height - margin.top - margin.bottom))
                .tickSizeOuter(3)
                .tickPadding(5)
                .ticks(xAxisTicks)
                .tickFormat(dateFormatter),
            y: d3
                .axisLeft(scales.response)
                .tickSizeInner(-(dimensions.width - margin.left - margin.right))
                .tickSizeOuter(3)
                .tickPadding(5)
                .ticks(2),
            y2: d3
                .axisRight(scales.requests)
                .tickSizeInner(-(dimensions.width - margin.left - margin.right))
                .tickSizeOuter(3)
                .tickPadding(5)
                .ticks(2),
        }
    }

    var _drawAxis = function(scales, axis) {
        var root = d3.select(svg).select('.axis')
        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        root.selectAll('.x')
            .call(axis.x)
            .attr('transform', 'translate(0, ' + (dimensions.height - margin.bottom - margin.top) + ')')

        root.selectAll('.y').call(axis.y)

        root.selectAll('.y2')
            .call(axis.y2)
            .attr('transform', 'translate(' + (dimensions.width - margin.left - margin.right) + ', 0)')

        d3.select(svg)
            .selectAll('.axis .x .tick text')
            .attr('class', d => timeScaleWeighted(d, axis.x.scale().domain()))
    }

    var _drawRangeMarkers = function(scales, rangeMarkers) {
        var root = d3.select(svg).select('.historySelection')
        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        let marker = root.selectAll('g.rangeMarker').data(rangeMarkers)
        // New data
        let newevent = marker.enter()

        let group = newevent.append('g').attr('class', function(marker) {
            return 'rangeMarker ' + marker.type
        })

        group
            .append('rect')
            .attr('class', 'covered left')
            .attr('y', 0)
            .attr('height', dimensions.height - margin.bottom - margin.top)
            .attr('clip-path', 'url(#historyClipPath)')

        group
            .append('rect')
            .attr('class', 'covered right')
            .attr('y', 0)
            .attr('height', dimensions.height - margin.bottom - margin.top)
            .attr('clip-path', 'url(#historyClipPath)')

        // Update existing nodes
        marker
            .select('rect.left')
            .attr('x', 0)
            .attr('width', function(marker) {
                return Math.max(0, scales.x(marker.start))
            })

        marker
            .select('rect.right')
            .attr('x', function(marker) {
                return scales.x(marker.end)
            })
            .attr('width', function(marker) {
                return Math.max(0, dimensions.width - margin.left - margin.right - scales.x(marker.end))
            })

        // Exiting node
        marker.exit().remove()
    }

    var _drawData = function(scales, lines) {
        let root = d3.select(svg).select('.lines')

        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        let pathFunction = d3
            .line()
            .x(function(point) {
                return scales.x(point.date)
            })
            .y(function(point) {
                return scales[point.axis](point.value)
            })
            .curve(d3.curveMonotoneX)
        let areaFunction = d3
            .area()
            .x(function(point) {
                return scales.x(point.date)
            })
            .y0(dimensions.height - margin.top - margin.bottom)
            .y1(function(point) {
                return scales[point.axis](point.value)
            })
            .curve(d3.curveMonotoneX)

        let line = root.selectAll('g.line').data(lines, function(line) {
            return line.name
        })

        // New data
        let newLine = line.enter()
        let group = newLine.append('g').attr('class', function(line) {
            return 'line ' + line.name
        })

        group
            .append('path')
            .attr('class', 'line')
            .attr('d', function(line) {
                return pathFunction(line.data)
            })
            .attr('clip-path', 'url(#historyClipPath)')
        group
            .append('path')
            .attr('class', 'fill')
            .attr('d', function(line) {
                return areaFunction(line.data)
            })
            .attr('clip-path', 'url(#historyClipPath)')

        // Update existing nodes
        line.select('path.line')
            .attr('d', function(line) {
                return pathFunction(line.data)
            })
            .attr('transform', null)
            .transition()
            .attr('transition', 'translate(' + scales.x(-1) + ')')
        line.select('path.fill')
            .attr('d', function(line) {
                return areaFunction(line.data)
            })
            .attr('transform', null)
            .transition()
            .attr('transition', 'translate(' + scales.x(-1) + ')')

        // Exiting node
        line.exit().remove()
    }

    var _drawScrollBar = function(brush) {
        if (!renderScrollbar) {
            return
        }

        let root = d3.select(svg).select('.scrollBar')

        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', 12)
            .attr('transform', 'translate(' + margin.left + ', ' + (dimensions.height - 12) + ')')

        let bar = root.selectAll('g.brush').data([brush], function() {
            return 'brush'
        })

        // New data
        let newBar = bar.enter()

        newBar
            .append('g')
            .attr('class', 'x brush')
            .call(brush)
            .selectAll('rect')
            .attr('y', 2)
            .attr('height', 8)

        // Update existing nodes
        bar.select('.brush').call(brush)

        // Exiting node
        bar.exit().remove()
    }

    var appendValue = function(dataSet) {
        if (dataSet.data.length < 2) {
            return dataSet
        }

        let previous = dataSet.data[dataSet.data.length - 2]
        let current = dataSet.data[dataSet.data.length - 1]
        let next = _.clone(current)

        let difference = moment.duration(moment(current.date).diff(moment(previous.date)))

        next.date = moment(next.date)
            .add(difference)
            .toDate()
        next.value = 0 // Not necessary, but the chart closes in a more sensible way

        dataSet.data[dataSet.data.length] = next

        return dataSet
    }

    this.draw = function() {
        _drawAxis(scales, _axis(scales))
        _drawRangeMarkers(scales, markers)
        _drawData(scales, lines)
        _drawScrollBar(brush)
    }

    const onBrush = () => {
        const selection = d3.event.selection.map(xCoordinate => scales.x2.invert(xCoordinate))
        scales.x.domain(selection)

        this.draw()
    }

    this.update = function(data, rangeMarkers) {
        lines = _.map(data, appendValue)
        scales = _scales(lines)
        markers = rangeMarkers || []

        const days = Math.floor(
            moment.duration(moment(lines[0].data[lines[0].data.length - 1].date).diff(moment(lines[0].data[0].date))).asDays(),
        )

        const scale = Math.max(days, 1) / daysOnScale
        const graphWidth = dimensions.width - margin.left - margin.right
        const lengthOfBrush = graphWidth * (1 / scale) // * -scale,

        if (!brush) {
            brush = d3
                .brushX()
                .extent([
                    [0, 0],
                    [graphWidth, 8],
                ])
                .on('brush', onBrush)
        }

        this.draw()

        // this is duplicated code from rect.left / rect.right from the draw() method
        //    reason for that, see https://github.com/tideways/apm/pull/7735#issuecomment-2358163071
        const selectedRange = {
            start: Math.max(0, scales.x(markers[0].start)),
            end: scales.x(markers[0].end),
        }

        const selectionLength = selectedRange.end - selectedRange.start

        // try to center selected section
        let start = selectedRange.start - (lengthOfBrush / 2 - selectionLength / 2)

        // scrollbar-start should not overflow to the left
        if (start < 0) {
            start = 0
        }

        // scrollbar-end should not overflow to the right
        // also, if nothing is selected, stay to the right edge
        if (start + lengthOfBrush > graphWidth || selectionLength === 0) {
            start = graphWidth - lengthOfBrush
        }

        brush.move(d3.select(svg).select('.scrollBar .brush'), [start, start + lengthOfBrush])

        // removes handle to resize the brush
        d3.selectAll('.scrollBar .brush>.handle').remove()
        // removes crosshair cursor
        d3.selectAll('.scrollBar .brush>.overlay').remove()

        if (!handlersRegistered) {
            handlersRegistered = true
            _registerEventHandlers(d3.select(svg).select('.lines'))
        }
    }

    this.destroy = function() {
        // remove all children from the svg.
        d3.select(svg)
            .selectAll('*')
            .remove()
    }
}

export default Chart
